// Lightweight GA4 analytics helpers used across the app.
// Events: generate_lead, chat_started, affiliate_click.
//
// Notes:
//  * Respects Consent Mode v2 — gtag will queue/skip if user hasn't accepted.
//  * Safe in SSR / when window is missing.

function safeGtag(...args) {
  if (typeof window === "undefined") return;
  try {
    window.dataLayer = window.dataLayer || [];
    if (typeof window.gtag === "function") {
      window.gtag(...args);
    } else {
      // Fall back to direct dataLayer push (Consent Mode handles delivery)
      window.dataLayer.push(args);
    }
  } catch {
    // swallow — analytics must never break the app
  }
}

// Dedup guard shared across trackAffiliateClick + global fallback tracker.
// Prevents double-firing when an anchor has both an inline onClick handler AND
// matches a URL pattern the global capture-phase listener would also catch.
// Key = affiliate_name + '|' + link_url. TTL = 500ms.
const AFFILIATE_DEDUP = new Map();
function shouldSkipDuplicate(name, url) {
  if (typeof window === "undefined") return false;
  const key = `${name}|${url || ""}`;
  const now = Date.now();
  const last = AFFILIATE_DEDUP.get(key);
  AFFILIATE_DEDUP.set(key, now);
  // Cleanup old entries occasionally
  if (AFFILIATE_DEDUP.size > 50) {
    for (const [k, ts] of AFFILIATE_DEDUP) {
      if (now - ts > 5000) AFFILIATE_DEDUP.delete(k);
    }
  }
  return last && now - last < 500;
}

/**
 * Fire when a qualified lead is generated (form submitted successfully).
 * In GA4 this is marked as the main conversion event.
 */
export function trackGenerateLead(extra = {}) {
  safeGtag("event", "generate_lead", {
    method: "form",
    ...extra,
  });
}

/**
 * (Deprecated) Fired once per session when the user sent their first chat message.
 * The chat feature has been removed; this function is kept as a no-op for backward
 * compatibility with any historical events already tracked.
 */
export function trackChatStarted(extra = {}) {
  // no-op
  void extra;
}

/**
 * Fire when the user clicks any affiliate link.
 * @param {string} name - canonical provider id (snake_case).
 *   e.g. "disneyland_paris" | "edreams" | "amazon" | "salas_vip" | "sncf" | "iati"
 * @param {object} extra - additional GA4 parameters (placement, item, etc.)
 *
 * Auto-injects page_location, page_title and link_url when available so all
 * affiliate events carry consistent context for GA4 analysis.
 */
export function trackAffiliateClick(name, extra = {}) {
  const auto = {};
  if (typeof window !== "undefined") {
    try {
      auto.page_location = window.location.href;
      auto.page_title = document.title;
    } catch {
      /* swallow */
    }
  }
  const resolvedName = name || "unknown";
  const linkUrl = extra.link_url || "";
  if (shouldSkipDuplicate(resolvedName, linkUrl)) return;
  safeGtag("event", "affiliate_click", {
    affiliate_name: resolvedName,
    ...auto,
    ...extra,
  });
}

/**
 * Map a URL to its canonical affiliate provider id. Kept in sync with the
 * per-page detectAffiliate helpers so the global fallback tracker and any
 * page-level handler produce the same `affiliate_name`.
 */
export function detectAffiliateFromUrl(href = "") {
  const url = (href || "").toLowerCase();
  if (!url) return null;
  // IATI Seguros — TradeDoubler campaign p=403039 / g=26173470
  if (url.includes("p=403039") || url.includes("g=26173470") || url.includes("iatiseguros")) return "iati";
  // Disneyland Paris official — TradeDoubler p=53148
  if (url.includes("disneylandparis.com") || url.includes("g(25243790)") || url.includes("p=53148")) return "disneyland_paris";
  // eDreams
  if (url.includes("g=25303216") || url.includes("p=15407") || url.includes("edreams")) return "edreams";
  // Iberia
  if (url.includes("tkqlhce.com") || url.includes("tqlkg.com") || url.includes("iberia.com")) return "iberia";
  // Transavia
  if (url.includes("click.transavia.com") || url.includes("transavia.com")) return "transavia";
  // Amazon
  if (url.includes("amzn.to") || url.includes("amazon.")) return "amazon";
  // Booking
  if (url.includes("booking.com")) return "booking";
  // GetYourGuide
  if (url.includes("getyourguide.com") || url.includes("gygrfr")) return "getyourguide";
  // Civitatis
  if (url.includes("civitatis.com")) return "civitatis";
  // Salas VIP / SNCF / El Corte Inglés / Magical Shuttle
  if (url.includes("salasvip")) return "salas_vip";
  if (url.includes("sncf")) return "sncf";
  if (url.includes("magicalshuttle") || url.includes("magical-shuttle")) return "magical_shuttle";
  if (url.includes("viajeselcorteingles") || url.includes("elcorteingles")) return "el_corte_ingles";
  // Generic TradeDoubler / CJ / Awin that didn't match a specific campaign
  if (
    url.includes("tradedoubler.com") ||
    url.includes("anrdoezrs.net") ||
    url.includes("jdoqocy.com") ||
    url.includes("dpbolvw.net") ||
    url.includes("kqzyfj.com") ||
    url.includes("awin1.com")
  ) {
    return "affiliate_generic";
  }
  return null;
}

/**
 * Global fallback: attach a document-level listener that fires
 * `affiliate_click` for any anchor whose href matches a known affiliate
 * network. Runs alongside inline onClick handlers — GA4 will still see one
 * event per click because inline handlers use the same helper (and we set a
 * dedup flag on the element to avoid double-firing for cases where both an
 * inline onClick and the fallback would fire in the same tick).
 */
export function installAffiliateClickTracker() {
  if (typeof window === "undefined") return;
  if (window.__encAffiliateTrackerInstalled) return;
  window.__encAffiliateTrackerInstalled = true;
  document.addEventListener(
    "click",
    (e) => {
      try {
        const a = e.target.closest && e.target.closest("a[href]");
        if (!a) return;
        // Dedup: if an inline onClick already handled this click, it should
        // have set a marker. If not, we detect and fire.
        if (a.__encAffiliateFired) return;
        const href = a.getAttribute("href") || "";
        const name = detectAffiliateFromUrl(href);
        if (!name) return;
        const placement =
          a.getAttribute("data-affiliate-placement") ||
          a.getAttribute("data-testid") ||
          a.getAttribute("aria-label") ||
          a.closest("[data-section]")?.getAttribute("data-section") ||
          "auto_detected";
        trackAffiliateClick(name, {
          placement,
          link_url: href,
        });
        // Prevent this same click from being tracked twice if the anchor
        // remounts and receives another synthetic event.
        a.__encAffiliateFired = true;
        setTimeout(() => { try { delete a.__encAffiliateFired; } catch { /* noop */ } }, 300);
      } catch {
        /* never break UX */
      }
    },
    true // capture phase — runs before React handlers may stopPropagation
  );
}

/**
 * Fire when the user clicks any WhatsApp link (wa.me / api.whatsapp.com).
 * Auto-attached via a global document-level listener — no need to add
 * onClick to every link individually.
 * @param {object} info - placement, page_path, prefilled_message snippet
 */
export function trackWhatsAppClick(info = {}) {
  safeGtag("event", "whatsapp_click", {
    method: "whatsapp",
    ...info,
  });
}

/**
 * One-shot installer that listens for clicks on any anchor with a WhatsApp
 * destination, anywhere in the document. Call once on app boot.
 */
export function installWhatsAppClickTracker() {
  if (typeof window === "undefined") return;
  if (window.__encWhatsAppTrackerInstalled) return;
  window.__encWhatsAppTrackerInstalled = true;  document.addEventListener(
    "click",
    (e) => {
      try {
        const a = e.target.closest && e.target.closest("a[href]");
        if (!a) return;
        const href = a.getAttribute("href") || "";
        if (!/wa\.me\/|api\.whatsapp\.com|whatsapp:\/\//i.test(href)) return;
        const placement =
          a.getAttribute("data-testid") ||
          a.getAttribute("aria-label") ||
          a.closest("[data-section]")?.getAttribute("data-section") ||
          "unknown";
        trackWhatsAppClick({
          placement,
          page_path: window.location.pathname + window.location.search,
          link_url: href,
        });
      } catch {
        /* never break UX */
      }
    },
    true // capture phase — runs before React handlers may stopPropagation
  );
}
